import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applyTeamRule, getTeamRulePreview } from '@/api/onsiteLiveTeams';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { ErrorBanner } from '../BranchRelationships/shared';
import { btnPrimary } from '../BranchRelationships/styles';
import { RULE_KEY, TEAMS_KEY } from './keys';

/**
 * The team rule against the stored teams (oscar.vOnsiteLiveUserTeam, plan §3f):
 * how many users each rule covers, and everyone whose stored team differs - so
 * whose approval limits would change. Apply stores the rule's team for exactly
 * the users listed here (and only if they still differ).
 *
 * Go-live gate (plan §3f): this list shows only expected moves before the first
 * apply, and before the 15-minute sync is switched to apply the rule itself.
 */
export function RulePreviewTab({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const query = useQuery({ queryKey: RULE_KEY, queryFn: ({ signal }) => getTeamRulePreview(signal) });
  const changes = query.data?.Changes ?? [];

  const apply = useMutation({
    mutationFn: () => applyTeamRule(changes.map((c) => c.OnsiteLiveUserId)),
    onSuccess: (r) => {
      setConfirm(false);
      setDone(`${r.Applied} of ${r.Listed} user(s) moved to the rule's team.`);
      void qc.invalidateQueries({ queryKey: RULE_KEY });
      void qc.invalidateQueries({ queryKey: TEAMS_KEY });
    },
  });

  if (query.isLoading) return <p className="text-sm text-darkGrey/60">Loading…</p>;
  if (query.isError || !query.data) return <ErrorBanner message={(query.error as Error | null)?.message || 'Could not load the preview.'} />;
  if (query.data.ViewMissing)
    return <ErrorBanner message="The team rule view (oscar.vOnsiteLiveUserTeam) is not on this database yet. Ask for the unnumbered view file to be run." />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-darkGrey/80">
        A department mapped to a team puts its people in that team; an unmapped department, or no department, puts them in Default
        (no authority). Shared logins, inactive users and hand-set teams (such as the Sime Industrial teams) are left as they are.
      </p>

      <div className="overflow-x-auto rounded border border-border bg-white">
        <table className="w-full text-sm" aria-label="Rule summary">
          <thead className="bg-navy text-left text-xs text-white">
            <tr><th className="px-3 py-2">Rule</th><th className="px-3 py-2 text-right">Users</th><th className="px-3 py-2 text-right">Team would change</th></tr>
          </thead>
          <tbody>
            {query.data.Summary.map((s, i) => (
              <tr key={s.Rule} className={i % 2 ? 'bg-[#F4F6F9]' : 'bg-white'}>
                <td className="px-3 py-1.5">{s.Rule}</td>
                <td className="px-3 py-1.5 text-right">{s.Users}</td>
                <td className={`px-3 py-1.5 text-right ${s.Differ > 0 ? 'font-semibold text-orange' : ''}`}>{s.Differ}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-base font-bold text-navy">Team would change ({changes.length})</h2>
          {canWrite && changes.length > 0 && (
            <button type="button" className={btnPrimary} onClick={() => { apply.reset(); setDone(null); setConfirm(true); }}>
              Apply to these {changes.length} users
            </button>
          )}
        </div>
        {done && <p role="status" className="mt-2 text-sm text-green">{done}</p>}
        {changes.length === 0 ? (
          <p className="mt-2 text-sm text-darkGrey/70">Every user&apos;s stored team matches the rule.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded border border-border bg-white">
            <table className="w-full text-sm" aria-label="Users whose team would change">
              <thead className="bg-navy text-left text-xs text-white">
                <tr>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Department</th>
                  <th className="px-3 py-2">Team now</th>
                  <th className="px-3 py-2">Rule team</th>
                  <th className="px-3 py-2">Why</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={c.OnsiteLiveUserId} className={i % 2 ? 'bg-[#F4F6F9]' : 'bg-white'}>
                    <td className="px-3 py-1.5">
                      <span className="font-medium text-navy">{c.Username}</span>
                      {c.FullName && <span className="block text-xs text-darkGrey/70">{c.FullName}</span>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">{c.DepartmentCode ?? '—'}</td>
                    <td className="px-3 py-1.5">{c.CurrentTeamId === null ? 'none' : `${c.CurrentTeamId} — ${c.CurrentTeam ?? ''}`}</td>
                    <td className="px-3 py-1.5 font-semibold">{c.RuleTeamId} — {c.RuleTeam ?? ''}</td>
                    <td className="px-3 py-1.5 text-xs text-darkGrey/70">{c.Rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirm && (
        <AlertDialog
          open
          onOpenChange={(o) => { if (!o) setConfirm(false); }}
          title={`Move ${changes.length} user(s) to the rule's team?`}
          description={
            <>
              Their approval limits in OnFlow change at once. Baseplan keeps its own copy of the team (Users.Team), which drives the
              20:00 PO and credit limit job: that write-back is not built yet, so update Baseplan for these users as you do today.
              Every change is audited.
            </>
          }
          confirmLabel="Apply"
          closeOnConfirm={false}
          onConfirm={() => apply.mutate()}
        >
          {apply.isError && <p role="alert" className="text-sm text-red">{apply.error instanceof ApiError ? apply.error.message : 'Could not apply the rule.'}</p>}
        </AlertDialog>
      )}
    </div>
  );
}
